// BargainFox returns portal: the Portal staff API (Portal-BE app/returns,
// mounted at /admin/returns). Three backend builders share the prefix:
//
//   ADMIN    settings, reasons, the list, a request's detail, photos, the
//            Orders page panel, approve / decline / complete / cancel / notes
//   LABELS   book a label, add one by hand, a label's PDF, void it, mark the
//            parcel received
//   EXCHANGE release an exchange, refund a card payment, an invoice PDF
//
// LABELS' and EXCHANGE's endpoints arrive with their own branches; until they
// are merged they answer 404 without a { code }, which `call` reports as
// notAvailable so the page can say "not available yet" instead of failing.
import { apiFetch } from "./apiFetch";

// ---- shapes -----------------------------------------------------------------------------

export type ReturnStatus =
    | "review" | "label_pending" | "awaiting_return" | "in_transit" | "received"
    | "refund_pending" | "replacement_sent" | "exchange_booked" | "completed" | "cancelled";
export type ReturnTab = "review" | "label" | "awaiting" | "transit" | "received" | "done" | "all";
export const RETURN_TABS: ReturnTab[] = ["review", "label", "awaiting", "transit", "received", "done", "all"];
export type Problem = "return" | "damaged" | "missing" | "wrong" | "notarrived";
export type Resolution = "refund" | "replacement" | "exchange";

export interface ReturnParcelBrief {
    parcel_no: number;
    status: string;
    carrier_code: string;
    tracking_number: string | null;
}

export interface ReturnRow {
    reference: string;
    status: ReturnStatus;
    tab: ReturnTab | null;
    problem: Problem;
    resolution: Resolution;
    entry: "link" | "direct";
    email: string;
    channel: string;
    orders: string[];
    first_title: string | null;
    lines: number;
    units: number;
    photos: number;
    needs_return: boolean;
    keep_it: boolean;
    review: boolean;
    claim: boolean;
    items_value_pence: number;
    refund_total_pence: number;
    payable_pence: number;
    service_id: string | null;
    labels: number | null;
    replacement_order_ids: string[];
    /** An exchange whose accepted items no longer cover the new ones (pence short), else null. */
    exchange_shortfall_pence?: number | null;
    parcels: ReturnParcelBrief[];
    created_at: string;
    updated_at: string;
    completed_at: string | null;
    cancelled_at: string | null;
}

export type TabCounts = Record<ReturnTab, number>;

export interface ReturnListPage {
    rows: ReturnRow[];
    total: number;
    page: number;
    page_size: number;
    counts: TabCounts;
}

export interface ReturnListQuery {
    tab: ReturnTab;
    q?: string;
    from?: string;
    to?: string;
    channel?: string;
    page?: number;
    page_size?: number;
}

export interface OrderBrief {
    order_id: string;
    order_number: string | null;
    warehouse: string | null;
    status: string | null;
    hold_reason: string | null;
    consolidated_into: string | null;
}

export interface ReturnLine {
    id: number;
    order_reference: string;
    order_id: string;
    order_item_id: number;
    line_position: number | null;
    sku: string;
    title: string;
    image_url: string | null;
    qty: number;
    reason: string;
    reason_label: string;
    fault: "ours" | "customer";
    detail: string | null;
    unit_price_pence: number;
    value_pence: number;
    delivery_refund_pence: number;
    replace_qty: number;
    refund_qty: number;
    pended_qty: number;
    photo_ids: string[];
    check: { accepted: number; rejected: number } | null;
}

export interface ReturnEvent {
    id: number;
    event: string;
    created_at: string;
    ip: string | null;
    by: string | null;
    detail: Record<string, any>;
}

export interface ReturnParcel {
    id?: number;
    parcel_no: number;
    service_id?: string;
    carrier_code: string;
    kind?: string;
    status: string;
    tracking_number: string | null;
    consignment_number?: string | null;
    collection_date?: string | null;
    drop_off_by?: string | null;
    booking_mode?: string | null;
    booking_error?: string | null;
    booked_at?: string | null;
    first_scan_at?: string | null;
    delivered_at?: string | null;
    last_event?: string | null;
    last_event_at?: string | null;
    has_label_pdf?: boolean;
    /** The carrier's tracking page (absent on an older BE; null for a training booking). */
    tracking_url?: string | null;
}

/** A box an order went out in (order_label, not voided), with the carrier's tracking page. */
export interface OutboundParcel {
    tracking_number: string;
    carrier: string | null;
    status: string;
    status_at: string | null;
    /** The carrier's own words: "LEFT WITH NEIGHBOUR", "Delivered". */
    description: string | null;
    tracking_url: string | null;
}

export interface ReturnPayment {
    id: number;
    provider: string;
    payment_id: string | null;
    amount_pence: number;
    currency: string;
    status: string;
    card_last4: string | null;
    card_scheme: string | null;
    invoice_number: string | null;
    invoice_issued_at: string | null;
    refunded_at: string | null;
    created_at: string;
    /** Its refunds, oldest first (absent on an older BE). */
    refunds?: ReturnPaymentRefund[];
}

/** A refund of a request's payment as the Returns page gets it (a return_refunds row). */
export interface ReturnPaymentRefund {
    id: number;
    amount_pence: number;
    kind: "refund" | "void";
    status: "requested" | "pending" | "refunded" | "declined" | "failed";
    reason: string;
    source: string;
    requested_by_name: string | null;
    error: string | null;
    /** The credit note's number, set once Checkout.com approved the refund (the PDF is headed "Credit Invoice"). */
    credit_note_number: string | null;
    credit_note_issued_at: string | null;
    settled_at: string | null;
    created_at: string;
}

export interface ReturnDetail {
    reference: string;
    status: ReturnStatus;
    tab: ReturnTab | null;
    problem: Problem;
    resolution: Resolution;
    entry: "link" | "direct";
    channel: string;
    channel_name: string;
    flags: { needs_return: boolean; keep_it: boolean; review: boolean; claim: boolean };
    customer: {
        email: string;
        entry: "link" | "direct";
        name: string | null;
        company: string | null;
        address: string[];
        phone: string | null;
        return_rate_pct: number | null;
        created_ip: string | null;
        user_agent: string | null;
    };
    /** `parcels`: the boxes it went out in (absent on an older BE). */
    orders: { order_reference: string; delivered_at: string | null; return_by: string | null; parts: OrderBrief[]; parcels?: OutboundParcel[] }[];
    replacement_orders: OrderBrief[];
    exchange_orders: OrderBrief[];
    lines: ReturnLine[];
    order_photos: { photo_id: string; order_reference: string }[];
    photos: { photo_id: string; mime_type: string; size_bytes: number; return_item_id: number | null; order_reference: string | null }[];
    money: {
        quoted: { items_value: number; vat: number; delivery_refund: number; postage: number; credit: number; refund_total: number; payable: number };
        refund_total: number;
        payable: number;
        service_id: string | null;
        labels: number | null;
        collection_date: string | null;
        auto_action_failed: string | null;
        /** Set by the exchange check when the accepted credit no longer covers the new items: nothing was charged. */
        exchange_shortfall?: { pence: number; accepted_credit_pence: number; basket_pence: number; paid_pence: number; rejected_units: number; at: string } | null;
    };
    next: { title?: string; lead?: string; next_steps?: { n: number; t: string; d: string }[] } | null;
    events: ReturnEvent[];
    notes: { id: number; text: string; by: string | null; created_at: string }[];
    parcels: ReturnParcel[];
    payments: ReturnPayment[];
    /**
     * book_label: book or attach the return label (label_pending, or an exchange with no booked label); older BEs omit it.
     * release_replacement: the replacement is held (auto-approve) and the request is open, so staff may send it now.
     */
    actions: { approve: boolean; decline: boolean; book_label?: boolean; complete: boolean; cancel: boolean; note: boolean; release_replacement?: boolean };
    created_at: string;
    updated_at: string;
    completed_at: string | null;
    cancelled_at: string | null;
    /** Window-closed contact-form messages on this request's orders (absent before migration 260). */
    contacts?: ReturnContact[];
    /** An exchange's new items and where they stand; null for any other request (absent on an older BE). */
    exchange?: ReturnExchange | null;
    /** An auto-approved replacement's hold; null for any other request (absent on an older BE). */
    replacement_hold?: ReplacementHold | null;
    /** Present when the warehouse check may charge the return postage; pence is what would be deducted. */
    postage_charge?: { pence: number } | null;
}

/** The return milestone an exchange order waits for; "booked" only on rows from before it was retired. */
export type ExchangeMilestone = "shipped" | "delivered" | "received" | "booked";

/**
 * An exchange (Portal-BE returnExchange.js). held = waiting for the return: its
 * order (exchange_orders) was raised at booking in the order status "exchange",
 * holding its stock; the release (the milestone, or staff "Release now") moves
 * it to Unfulfilled. A request booked under round 5 (2026-10-30) may have no
 * order yet: its release raises it (stock_short when it has sold out).
 */
export interface ReturnExchange {
    status: "held" | "released" | "cancelled";
    release_when: ExchangeMilestone;
    basket: { product_id: string; sku: string; title: string; qty: number; unit_price_pence: number; image_url: string | null }[];
    basket_value_pence: number;
    /**
     * How the new items go (Jack, 2026-10-30): standard (free) or express, paid at
     * BargainFox's rate or free with FastFox. Absent from an older BE: standard.
     */
    delivery?: { speed: "standard" | "express"; fee_pence: number; fastfox: boolean };
    released_at: string | null;
    /** The milestone that released it, or "staff". */
    released_via: string | null;
    cancelled_at: string | null;
    cancel_reason: string | null;
    /** Staging: the created order stays on a manual hold. */
    test_hold: boolean;
    /** A release found too little stock: no order was created, staff decide. */
    stock_short: { at: string; missing: { sku: string; needed: number; available: number }[] } | null;
}

/**
 * An auto-approved replacement (Settings › Returns auto_approve_returns, Jack,
 * 2026-10-31): worth the instant replacement limit or more, its item coming
 * back. Its order (replacement_orders) is raised at once in the order status
 * "exchange", holding its stock, and released on the return's first carrier
 * scan, or by staff ("Send replacement now"). No scan within
 * exchange_cancel_days: it is cancelled.
 */
export interface ReplacementHold {
    status: "held" | "released" | "cancelled";
    release_when: "shipped";
    held_at: string;
    released_at: string | null;
    /** The return milestone that released it (shipped / delivered / received), or "staff". */
    released_via: string | null;
    cancelled_at: string | null;
}

/** A message from the returns site's window-closed contact form (Portal-BE returnContacts.js). */
export interface ReturnContact {
    id: number;
    order_reference: string;
    email: string;
    name: string | null;
    reason: "faulty" | "missing" | "notarrived" | "other";
    reason_label: string;
    message: string;
    photo_ids: string[];
    /** gorgias = a ticket in Gorgias (production); email = sent to the support inbox. */
    delivery: "pending" | "gorgias" | "email" | "failed";
    gorgias_url: string | null;
    created_at: string;
}

export interface OrderReturn {
    reference: string;
    relation: "original" | "replacement" | "exchange";
    status: ReturnStatus;
    tab: ReturnTab | null;
    problem: Problem;
    resolution: Resolution;
    created_at: string;
    refund_total_pence: number;
    payable_pence: number;
    replacement_order_ids: string[];
    exchange_order_ids: string[];
    invoices: { invoice_number: string; amount_pence: number; status: string; issued_at: string | null }[];
    /** One per approved refund of the request's top-up (migration 265); absent from an older BE. */
    credit_notes?: { credit_note_number: string; amount_pence: number; issued_at: string | null }[];
}

/**
 * A refund of a BargainFox Returns card payment (Portal-BE, Orders page
 * GET /orders/:order_id/returns-payment). Money in pence.
 */
export interface ReturnsRefund {
    id: number;
    amount_pence: number;
    kind: "refund" | "void";
    status: "requested" | "pending" | "refunded" | "declined" | "failed";
    /** Printed on the credit note the customer gets (its line description). */
    reason: string;
    source: "staff" | "returns_page" | "exchange_cancelled" | "dashboard" | "earlier";
    created_at: string;
    settled_at: string | null;
    by_name: string | null;
    /** Set once status is "refunded". */
    credit_note_number: string | null;
    /** Why it was declined / failed / not yet confirmed. */
    error: string | null;
    /** The refund email with the credit note was sent. */
    customer_emailed: boolean;
    /** Whether an email was asked for. */
    notify_customer: boolean;
}

/** A card top-up paid on the BargainFox Returns site, as the exchange order(s) it paid for see it. */
export interface ReturnsPayment {
    payment_row_id: number;
    return_reference: string;
    exchange_order_ids: string[];
    /** What the customer paid. */
    amount_pence: number;
    currency: "GBP";
    paid_at: string | null;
    /** "Visa ending 4242", "Apple Pay", "PayPal", … */
    paid_by: string;
    /** Checkout.com pay_… */
    payment_id: string | null;
    /** approved | captured | refund_pending | refunded | … */
    status: string;
    invoice: { number: string; issued_at: string | null } | null;
    /** Confirmed refunds. */
    refunded_pence: number;
    /** Refunds asked for, not confirmed yet. */
    pending_pence: number;
    /** amount_pence − refunded − pending, never below 0. */
    refundable_pence: number;
    /** A refund can be issued right now. */
    can_refund: boolean;
    /** Shown when can_refund is false and refundable_pence > 0. */
    refund_blocked_reason: string | null;
    /** Oldest first. */
    refunds: ReturnsRefund[];
}

// ---- calling ----------------------------------------------------------------------------

/** What every read and action answers: the JSON body, or why not. */
export interface ApiResult<T> {
    ok: boolean;
    status: number;
    data: T | null;
    /** BE error code ({ code, message }), when there is one. */
    code: string | null;
    message: string | null;
    /** The endpoint does not exist on this BE yet (LABELS / EXCHANGE not merged). */
    notAvailable: boolean;
    /** The whole error body, for field-level errors. */
    body: any;
}

// A route that doesn't exist answers 404 from Express with no JSON { code };
// our handlers' own "not found" always carries code "not_found".
export const isMissingRoute = (status: number, body: any) =>
    status === 404 && !(body && typeof body === "object" && typeof body.code === "string");

const call = async <T,>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> => {
    try {
        const res = await apiFetch(path, init);
        const body = await res.json().catch(() => null);
        return {
            ok: res.ok,
            status: res.status,
            data: res.ok ? (body as T) : null,
            code: body && typeof body.code === "string" ? body.code : null,
            message: body && typeof body.message === "string" ? body.message : null,
            notAvailable: isMissingRoute(res.status, body),
            body,
        };
    } catch {
        return { ok: false, status: 0, data: null, code: "network", message: null, notAvailable: false, body: null };
    }
};

const json = (method: string, body: unknown): RequestInit => ({
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
});

const ref = (reference: string) => encodeURIComponent(reference);

export const listReturns = (q: ReturnListQuery) => {
    const params = new URLSearchParams();
    params.set("tab", q.tab);
    if (q.q) params.set("q", q.q);
    if (q.from) params.set("from", q.from);
    if (q.to) params.set("to", q.to);
    if (q.channel) params.set("channel", q.channel);
    params.set("page", String(q.page ?? 1));
    params.set("page_size", String(q.page_size ?? 25));
    return call<ReturnListPage>(`/admin/returns?${params.toString()}`);
};
export const getReturn = (reference: string) => call<ReturnDetail>(`/admin/returns/${ref(reference)}`);
export const returnsForOrder = (orderId: string) => call<{ returns: OrderReturn[]; contacts?: ReturnContact[] }>(`/admin/returns/by-order/${encodeURIComponent(orderId)}`);

// The BargainFox Returns card payment behind an exchange order (Orders page,
// guard admin.orders; the refund needs admin.orders.refund_issue write).
export const returnsPaymentForOrder = (orderId: string) =>
    call<{ payments: ReturnsPayment[] }>(`/orders/${encodeURIComponent(orderId)}/returns-payment`);
/** 201 { refund, payment }; refund.status "refunded" when Checkout.com confirmed it, else "pending" / "requested". */
export const refundReturnsPayment = (orderId: string, body: { payment_row_id: number; amount_pence: number; reason: string; notify_customer?: boolean }) =>
    call<{ refund: ReturnsRefund; payment: ReturnsPayment }>(`/orders/${encodeURIComponent(orderId)}/returns-payment/refunds`, json("POST", body));
/** The sales invoice (INV-R-…) or a credit note (CN-R-…) of a payment on this order. */
export const returnsPaymentDocumentPath = (orderId: string, number: string) =>
    `/orders/${encodeURIComponent(orderId)}/returns-payment/documents/${encodeURIComponent(number)}.pdf`;

// ADMIN actions
export const approveReturn = (reference: string, body: { resolution: "replacement" | "refund"; message?: string }) =>
    call<any>(`/admin/returns/${ref(reference)}/approve`, json("POST", body));
export const declineReturn = (reference: string, body: { message: string }) =>
    call<any>(`/admin/returns/${ref(reference)}/decline`, json("POST", body));
/** charge_postage: the customer's reason wasn't true, so the return postage is charged; `message` is then required (400 message_required). */
export const completeReturn = (reference: string, body: { lines: { return_item_id: number; accept_qty: number }[]; message?: string; charge_postage?: boolean }) =>
    call<{ reference: string; status: string; postage_charged_pence?: number; [key: string]: any }>(`/admin/returns/${ref(reference)}/complete`, json("POST", body));
/** `reason` is staff-only; `message` (optional) goes to the customer in the return_cancelled email. */
export const cancelReturn = (reference: string, body: { reason?: string; message?: string }) =>
    call<{ reference: string; status: string; parcels_to_void?: ReturnParcelBrief[]; parcels_moving?: ReturnParcelBrief[]; already?: boolean;
        /** APC consignments the cancel voided (or couldn't). */
        labels_voided?: { waybill: string; outcome: "voided" | "already" | "manifested" | "failed" | "not_needed"; error?: string }[] }>(
        `/admin/returns/${ref(reference)}/cancel`, json("POST", body));
export const addReturnNote = (reference: string, text: string) =>
    call<any>(`/admin/returns/${ref(reference)}/notes`, json("POST", { text }));

// LABELS (contract: returnsAdminLabels.router.js)
export const bookLabel = (reference: string) => call<any>(`/admin/returns/${ref(reference)}/labels/book`, json("POST", {}));
export const addManualLabel = (reference: string, form: FormData) =>
    call<any>(`/admin/returns/${ref(reference)}/labels/manual`, { method: "POST", body: form });
export const voidLabel = (reference: string, parcelNo: number) =>
    call<any>(`/admin/returns/${ref(reference)}/labels/${parcelNo}/cancel`, json("POST", {}));
export const markReceived = (reference: string) => call<any>(`/admin/returns/${ref(reference)}/received`, json("POST", {}));
export const labelPdfPath = (reference: string, parcelNo: number) => `/admin/returns/${ref(reference)}/labels/${parcelNo}.pdf`;

// EXCHANGE (contract: returnsAdminExchange.router.js)
/** Creates the exchange order(s) now; 409 { code: "stock_short", missing } when stock is short. Also sends a held (auto-approved) replacement now. */
export const releaseExchange = (reference: string) => call<any>(`/admin/returns/${ref(reference)}/exchange/release`, json("POST", {}));
export const refundPayment = (reference: string) => call<any>(`/admin/returns/${ref(reference)}/payment/refund`, json("POST", {}));
export const invoicePdfPath = (invoiceNumber: string) => `/admin/returns/invoices/${encodeURIComponent(invoiceNumber)}.pdf`;
export const creditNotePdfPath = (creditNoteNumber: string) => `/admin/returns/credit-notes/${encodeURIComponent(creditNoteNumber)}.pdf`;

export const photoPath = (photoId: string) => `/admin/returns/photos/${encodeURIComponent(photoId)}`;

/**
 * Open a file the API serves behind the staff session (label PDF, invoice)
 * in a new tab. A plain link can't carry the bearer token, so it is fetched
 * and handed over as a blob URL.
 */
export const openProtectedFile = async (path: string): Promise<{ ok: boolean; notAvailable: boolean; status: number }> => {
    // Open the tab inside the click so the popup blocker allows it.
    const win = window.open("", "_blank");
    try {
        const res = await apiFetch(path);
        if (!res.ok) {
            win?.close();
            const body = await res.json().catch(() => null);
            return { ok: false, status: res.status, notAvailable: isMissingRoute(res.status, body) };
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        if (win) win.location.href = url;
        else window.location.assign(url);
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return { ok: true, status: res.status, notAvailable: false };
    } catch {
        win?.close();
        return { ok: false, status: 0, notAvailable: false };
    }
};

// ---- Settings › Returns -----------------------------------------------------------------

/** A return service. max_kg / max_len_cm absent = the carrier's own limits (Integrations › Carriers). */
export interface ReturnService {
    id: string;
    carrier: string;
    name: string;
    fee_pence: number;
    enabled: boolean;
    max_kg?: number | null;
    max_len_cm?: number | null;
    /** The largest box it takes, any orientation (cm); absent = no box limit here. */
    max_dims_cm?: [number, number, number] | null;
    postcode_include: string[];
    postcode_exclude: string[];
}

export interface ReturnsSettings {
    enabled: boolean;
    terms_url: string | null;
    help_url: string | null;
    return_warehouse: string;
    return_address: Record<string, string | null>;
    invoice_seller: Record<string, string | null>;
    reply_to: string | null;
    /** The From address on every customer email the returns portal sends (a domain SES can send from). */
    from_address: string;
    /** Where the window-closed contact form goes when Gorgias isn't configured. */
    support_inbox_email: string;
    /** One address emailed (at most hourly) when the returns portal records errors; null = no alert emails. */
    error_alert_email: string | null;
    /** The "Return or report a problem" button in the BargainFox shipping email. */
    shipping_email_returns_link: boolean;
    /** The contact form creates Gorgias tickets (needs GORGIAS_* on the server too); off = emails the support inbox. */
    contact_via_gorgias: boolean;
    window_days: number;
    /** Damaged, faulty, wrong or missing items; null = the same as window_days. */
    problem_window_days: number | null;
    /** The Royal Mail label pool: warn below this many usable labels. */
    label_pool_low_stock: number;
    /** Never issue a pool label older than this; null = no limit. */
    label_pool_max_age_days: number | null;
    /** Our Royal Mail account: uploaded labels for any other are refused. */
    royal_mail_oba: string;
    /** INPOST: the account returns are booked on (e.g. ooh). */
    inpost_return_account?: string;
    /** INPOST: days the customer has to drop the parcel off; null = InPost's default. */
    inpost_drop_off_days?: number | null;
    /** INPOST: how far to look for lockers, and how many to show. */
    inpost_locker_radius_km?: number;
    inpost_lockers_shown?: number;
    /** APC: days after the booked drop-off day before an unused depot return counts as missed. */
    depot_dropoff_grace_days?: number;
    grace_days: number;
    no_scan_days: number;
    not_arrived_working_days: number;
    /** Returnless up to, by reason (Jack, 2026-10-30); 0 = never. */
    returnless_damaged_pence: number;
    returnless_wrong_pence: number;
    returnless_change_of_mind_pence: number;
    /** Who may keep items, over this many days (365 = a rolling 12 months). */
    returnless_window_days: number;
    /** From this many orders in the window the return rate decides; below it, the allowance. */
    returnless_rate_min_orders: number;
    /** Keep-it requests a customer below returnless_rate_min_orders gets in the window. */
    returnless_small_allowance: number;
    returnless_max_return_rate_pct: number;
    returnless_refunds: boolean;
    returnless_replacements: boolean;
    refund_original_delivery: boolean;
    deduct_return_postage: boolean;
    instant_replacement_limit_pence: number;
    /** At or over the instant limit with the item coming back: no review. The label is booked at once and the replacement held until the return's first carrier scan (default on; absent on an older BE). */
    auto_approve_returns?: boolean;
    refund_review_limit_pence: number;
    /** Missing / not arrived (2026-11-02): every one to a person; earlier ones allowed below the order count. */
    review_missing_requests?: boolean;
    missing_small_allowance?: number;
    /** The exchange shop's snapshot refresh (migration 275): 0 = off, else 5-60 minutes. */
    exchange_catalogue_refresh_minutes?: number;
    exchanges_enabled: boolean;
    exchange_min_stock: number;
    /** Express delivery of the new items, pence: one fee whatever the basket weighs; FastFox members pay nothing. */
    exchange_express_pence: number;
    /** When the exchange order moves Exchange → Unfulfilled: "shipped" | "delivered" | "received" ("booked" is no longer accepted). */
    exchange_tiers: { under_pence: number | null; when: string }[];
    /** The one limit on time in Exchange: the return not sent (no carrier scan) this many days after booking → cancelled. */
    exchange_cancel_days: number;
    ranking_window_days: number;
    ranking_price_weight: number;
    ranking_min_units: number;
    top_seller_badges: number;
    max_labels: number;
    services: ReturnService[];
    token_account_minutes: number;
    token_email_days: number;
    session_idle_minutes: number;
    lookup_ip_failures: number;
    lookup_order_failures: number;
    captcha_after_failures: number;
    mask_direct_sessions: boolean;
    /** Settings other builders add later are carried through untouched. */
    [key: string]: unknown;
}

/** An InPost account returns can be booked on. enabled: false = off for OUTBOUND labels only (Pembroke's ooh is returns-only). */
export interface InpostAccountOption {
    id: number;
    warehouse: string;
    account_label: string;
    enabled: boolean;
}

export interface ReturnsSettingsPayload {
    channel: string;
    settings: ReturnsSettings;
    overrides: Partial<ReturnsSettings>;
    defaults: ReturnsSettings;
    updated_at: string | null;
    updated_by: string | null;
    /** The records some settings point at (absent on an older BE: those fields stay free text). */
    options?: { warehouses: string[]; inpost_accounts: InpostAccountOption[] };
}

export interface ReturnReason {
    code: string;
    problem: Problem;
    label: string;
    fault: "ours" | "customer";
    needs_detail: boolean;
    needs_photo: boolean;
    detail_label: string | null;
    detail_placeholder: string | null;
    sort_order: number;
    enabled: boolean;
    fixed: boolean;
}

export const getReturnsSettings = () => call<ReturnsSettingsPayload>(`/admin/returns/settings`);
export const patchReturnsSettings = (patch: Record<string, unknown>) =>
    call<{ channel: string; settings: ReturnsSettings }>(`/admin/returns/settings`, json("PATCH", patch));
export const getReturnReasons = () => call<{ reasons: ReturnReason[] }>(`/admin/returns/reasons`);
export const createReturnReason = (body: Partial<ReturnReason>) => call<{ reason: ReturnReason }>(`/admin/returns/reasons`, json("POST", body));
export const updateReturnReason = (code: string, body: Partial<ReturnReason>) =>
    call<{ reason: ReturnReason }>(`/admin/returns/reasons/${encodeURIComponent(code)}`, json("PATCH", body));
export const deleteReturnReason = (code: string) => call<any>(`/admin/returns/reasons/${encodeURIComponent(code)}`, { method: "DELETE" });

/**
 * The settings PATCH answers 400 { errors: [{ key, message }] }; the service
 * list's messages start with the service id ("apc: fee_pence must be ...").
 * Turn that into field keys the form can place: "window_days",
 * "services.apc", or "_" for anything without a key.
 */
export const fieldErrors = (body: any): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const e of Array.isArray(body?.errors) ? body.errors : []) {
        const key = typeof e?.key === "string" ? e.key : "_";
        const message = typeof e?.message === "string" ? e.message : "invalid";
        if (key === "services") {
            const m = /^([a-z][a-z0-9_-]{0,31}):\s*(.*)$/.exec(message);
            if (m) { out[`services.${m[1]}`] = m[2]; continue; }
        }
        out[key] = message;
    }
    return out;
};

// ---- formatting ----------------------------------------------------------------------------

/** 1299 → "£12.99" (pence in). */
export const gbp = (pence: number | null | undefined) => {
    const p = Math.round(Number(pence) || 0);
    const abs = Math.abs(p);
    return `${p < 0 ? "-" : ""}£${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
};

/** "12.99" → 1299; null when it isn't a money amount. */
export const penceFrom = (text: string): number | null => {
    const s = String(text ?? "").trim().replace(/^£/, "");
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
    return Math.round(Number(s) * 100);
};

/** Which tab a status lives on (mirrors returnsAdminQueries.TABS on the BE). */
export const TAB_OF_STATUS: Record<ReturnStatus, ReturnTab> = {
    review: "review",
    label_pending: "label",
    awaiting_return: "awaiting",
    exchange_booked: "awaiting",
    in_transit: "transit",
    received: "received",
    refund_pending: "done",
    replacement_sent: "done",
    completed: "done",
    cancelled: "done",
};

// ---- the Royal Mail return label pool (Portal-BE labelPool.js, migration 261) -----------------

export type PoolStatus = "available" | "issued" | "downloaded" | "received" | "void";

export interface LabelPoolSummary {
    carrier: string;
    service: string;
    counts: Record<PoolStatus, number>;
    /** Available and not past the maximum label age. */
    available_usable: number;
    low_stock_threshold: number;
    low_stock: boolean;
    max_age_days: number | null;
    /** This instance hands out real labels (production); elsewhere specimens. */
    live: boolean;
    batches: { id: number; filename: string; page_count: number; new_count: number; duplicate_count: number; rejected_count: number; first_tracking: string | null; last_tracking: string | null; uploaded_by_name: string | null; uploaded_at: string }[];
    /** Migration 261 isn't applied on this database. */
    unavailable?: boolean;
}

export interface PoolLabel {
    id: number;
    tracking_number: string;
    mailmark_ref: string | null;
    account_number: string | null;
    route_code: string | null;
    status: PoolStatus;
    batch_id: number;
    page_no: number;
    issued_at: string | null;
    first_downloaded_at: string | null;
    last_downloaded_at: string | null;
    download_count: number;
    received_at: string | null;
    received_by_name: string | null;
    void_reason: string | null;
    voided_at: string | null;
    voided_by_name: string | null;
    return_reference: string | null;
    return_status: string | null;
}

export interface PoolUploadResult {
    filename: string;
    error?: string;
    sha256?: string;
    page_count?: number;
    new?: number;
    duplicate?: number;
    rejected?: number;
    rejected_pages?: { page: number; reason: string; tracking?: string }[];
    duplicate_pages?: { page: number; tracking: string }[];
    first_tracking?: string | null;
    last_tracking?: string | null;
    batch_id?: number | null;
    warnings?: string[];
}

export const getLabelPoolSummary = () => call<LabelPoolSummary>(`/admin/returns/label-pool/summary`);
export const listPoolLabels = (q: { q?: string; status?: string; limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams();
    if (q.q) params.set("q", q.q);
    if (q.status) params.set("status", q.status);
    if (q.limit) params.set("limit", String(q.limit));
    if (q.offset) params.set("offset", String(q.offset));
    const qs = params.toString();
    return call<{ labels: PoolLabel[]; more: boolean }>(`/admin/returns/label-pool/labels${qs ? `?${qs}` : ""}`);
};
export const uploadPoolLabels = (files: File[]) => {
    const form = new FormData();
    for (const f of files) form.append("files", f, f.name);
    return call<{ files: PoolUploadResult[]; summary: LabelPoolSummary }>(`/admin/returns/label-pool/upload`, { method: "POST", body: form });
};
export const voidPoolLabel = (id: number, reason: string) => call<{ id: number; status: string }>(`/admin/returns/label-pool/labels/${id}/void`, json("POST", { reason }));
export const poolLabelPdfPath = (id: number) => `/admin/returns/label-pool/labels/${id}/pdf`;

// ---- error monitoring: the Returns page's Errors tab (Portal-BE returnErrorsAdmin.js) ---------

/** Where an error happened. The BE may add more: an area not listed here shows as its raw value. */
export const RETURN_ERROR_AREAS = ["site", "api", "labels", "payments", "tracking", "emails", "exchange", "contact", "staff", "jobs", "config"] as const;
export type ReturnErrorArea = typeof RETURN_ERROR_AREAS[number];

export interface ReturnErrorRow {
    id: number;
    created_at: string;
    /** One of RETURN_ERROR_AREAS, or a value this FE doesn't know yet. */
    area: string;
    /** Short code of the place that failed ("APC booking"). */
    where: string;
    message: string;
    /** What a person must do about it, when anything. */
    action: string | null;
    /** The return request it's tied to, when it is. */
    reference: string | null;
    order_reference: string | null;
    /** N more identical errors were folded into this row (it happened N + 1 times). */
    repeats: number;
    /** When staff marked it seen, else null. It happening again clears the mark (the BE does that). */
    seen_at: string | null;
    /** Who marked it seen; may be null even when seen_at is set. */
    seen_by: string | null;
    detail: {
        code?: string;
        stack?: string;
        kind?: string;
        page?: string;
        status?: number;
        api_path?: string;
        user_agent?: string;
        build?: string;
        instance?: string;
        [key: string]: unknown;
    };
}

export interface ReturnErrorsPage {
    /** The environment these errors came from (staging, production and laptops share one database). */
    env: string;
    days: number;
    /** The seen filter, echoed: "new" = only errors not yet marked seen. Absent from a BE before 2026-10-31. */
    seen?: ReturnErrorsSeen;
    counts: {
        /** All areas, this env, seen or not. */
        last_24h: number;
        last_7d: number;
        /** Not yet marked seen in the last 7 days, whatever the filters: the tab badge. Absent from a BE before 2026-10-31. */
        unseen?: number;
        /** Within `days` and `seen`, ignoring the area filter. */
        by_area: Record<string, number>;
    };
    /** Rows matching days + area + seen. */
    total: number;
    page: number;
    page_size: number;
    /** Settings › Returns error_alert_email; null = no alert emails. */
    alert_email: string | null;
    rows: ReturnErrorRow[];
}

/** "new" (the default) = only errors not yet marked seen; "all" = seen ones too. */
export type ReturnErrorsSeen = "new" | "all";

export interface ReturnErrorsQuery {
    /** 1-90, default 7. */
    days?: number;
    area?: string;
    /** Omitted = "new". */
    seen?: ReturnErrorsSeen;
    page?: number;
    /** 1-100, default 50. */
    page_size?: number;
}

export const getReturnErrors = (q: ReturnErrorsQuery = {}) => {
    const params = new URLSearchParams();
    params.set("days", String(q.days ?? 7));
    if (q.area) params.set("area", q.area);
    if (q.seen) params.set("seen", q.seen);
    params.set("page", String(q.page ?? 1));
    params.set("page_size", String(q.page_size ?? 50));
    return call<ReturnErrorsPage>(`/admin/returns/errors?${params.toString()}`);
};

/** Mark errors seen: these rows (1-500 ids), or every unseen one of a period (days 1-90, default 7) and area.
 *  `seen: false` marks them not seen again. Needs admin.returns write. */
export type MarkReturnErrorsSeenBody =
    | { ids: number[]; seen?: boolean }
    | { all: true; area?: string; days?: number; seen?: boolean };

export interface MarkReturnErrorsSeenResult {
    marked: number;
    /** The new badge number: errors not yet marked seen in the last 7 days. */
    unseen: number;
}

export const markReturnErrorsSeen = (body: MarkReturnErrorsSeenBody) =>
    call<MarkReturnErrorsSeenResult>(`/admin/returns/errors/seen`, json("POST", body));

// ---- Returned to sender (Portal-BE app/returns/services/returnedParcel.js) -------------------
// A dispatched parcel that came back: scan its label on the Returns page, put what was in it on
// Pending Refund. Money is in the order's own currency, in minor units (Jobalots sells in eight).

export interface ReturnedParcelLine {
    /** null: a SKU in the box that is on no line of the order (shown, never refundable). */
    order_item_id: number | null;
    line_position: number | null;
    sku: string;
    title: string | null;
    image_url: string | null;
    unit_price_minor: number;
    /** The order line's quantity. */
    quantity: number;
    /** Units of the line in this parcel (the whole line when the contents were not recorded). */
    in_parcel: number;
    refunded: number;
    pending: number;
    /** What can still be refunded from this parcel. */
    refundable: number;
    /** A consolidation master's line: the customer order it belongs to. */
    part_order_id: string | null;
    part_order_number: string | null;
}

export type ReturnedParcelRefuseCode = "not_channel" | "replacement_order" | "label_voided" | "order_status" | "nothing_left";

export interface ReturnedParcel {
    tracking_number: string;
    carrier: string | null;
    service: string | null;
    /** order_label.status: created | in_transit | out_for_delivery | delivered | exception | returned | voided. */
    status: string | null;
    status_updated_at: string | null;
    carrier_status: string | null;
    tracking_url: string | null;
    shipped_at: string | null;
    box_position: number | null;
    parcel_count: number;
    /** The carrier says it was returned to sender (staff can refund either way). */
    returned_to_sender: boolean;
    contents_recorded: boolean;
    order: {
        order_id: string;
        order_number: string | null;
        status: string | null;
        channel_code: string | null;
        channel_name: string | null;
        channel_reference: string | null;
        warehouse: string | null;
        currency: string;
        /** What the customer paid for delivery on this order: never refunded here, written in the order note. */
        delivery_paid_minor: number;
        is_consolidation_master: boolean;
        consolidated_into: string | null;
        parts: { order_id: string; order_number: string | null; channel_reference: string | null }[];
        replacement_of: { order_id: string; order_number: string | null } | null;
    };
    customer: { name: string | null; email: string | null; postcode: string | null };
    lines: ReturnedParcelLine[];
    refundable_units: number;
    refundable_minor: number;
    can_refund: boolean;
    refuse: { code: ReturnedParcelRefuseCode | string; message: string; [key: string]: any } | null;
    /** Earlier refunds from this parcel, oldest first. */
    /** reason: "Returned to sender" or the typed reason (null on refunds from before 2026-11-03). */
    history: { at: string; by: string | null; units: number; amount_minor: number; reason?: string | null }[];
}

export interface ReturnedParcelLookup {
    found: boolean;
    code: string;
    matched_by?: "tracking_number" | "label_barcode";
    /** The scanned label is no longer on its order (relabelled): these are the order's parcels now. */
    superseded?: boolean;
    parcels?: ReturnedParcel[];
    /** Not found, but the label's order was: it holds no dispatched parcel now. */
    order?: { order_id: string; order_number: string | null; status: string | null };
}

export interface ReturnedParcelRefundResult {
    /** Nothing was left to refund (a second press): nothing was written. */
    already?: boolean;
    units: number;
    amount_minor: number;
    currency: string;
    delivery_paid_minor?: number;
    /** The order now on Orders › Pending Refund. */
    pending_on?: { order_id: string; order_number: string | null };
    /** The customer orders the refund is for (a consolidation master's parts). */
    orders?: { order_id: string; order_number: string | null; channel_reference: string | null; units: number; amount_minor: number }[];
    lines?: { order_item_id: number; sku: string; units: number }[];
    parcel: ReturnedParcel | null;
}

/** The dispatched parcel(s) a scanned or typed code stands for (admin.returns read). */
export const lookupReturnedParcel = (code: string) =>
    call<ReturnedParcelLookup>(`/admin/returns/returned-parcel?code=${encodeURIComponent(code)}`);
/**
 * Put a parcel's items on Pending Refund (admin.returns write); `lines` default: everything still refundable.
 * A reason is required (Jack, 2026-11-03): reason_code "returned_to_sender" (the quick button) or a typed `reason`.
 */
export const refundReturnedParcel = (body: {
    tracking_number: string;
    reason_code?: "returned_to_sender";
    reason?: string;
    lines?: { order_item_id: number; qty: number }[];
}) =>
    call<ReturnedParcelRefundResult>(`/admin/returns/returned-parcel/refund`, json("POST", body));

const CURRENCY_SYMBOLS: Record<string, string> = { GBP: "£", EUR: "€", USD: "$" };

/** 1299, "GBP" → "£12.99"; 1299, "PLN" → "12.99 PLN" (minor units in). */
export const money = (minor: number | null | undefined, currency: string | null | undefined) => {
    const code = (currency || "GBP").toUpperCase();
    const p = Math.round(Number(minor) || 0);
    const abs = Math.abs(p);
    const amount = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
    const sign = p < 0 ? "-" : "";
    return CURRENCY_SYMBOLS[code] ? `${sign}${CURRENCY_SYMBOLS[code]}${amount}` : `${sign}${amount} ${code}`;
};
